import { useAuthenticated, usePageSectionsQuery } from "@gettactic/hooks";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import {
  PageOptions,
  useDashboardFilters
} from "@/contexts/DashboardStateContext";
import { useEffect, useRef, useState } from "react";
import {
  DEFAULT_DAYS_COUNT,
  pageOptionToTab,
  tabToPageOption
} from "@webV2/dashboard/defines";
import { useRouter } from "next/router";
import { useTeamAttendanceFilters } from "@webV2/components/TeamSchedule/hooks/useTeamAttendanceFilters";
import { goToMap } from "@/lib/utils-map";
import { useDashboardLocation } from "@/hooks/useDashboardLocation";
import { canScheduleOthers } from "@gettactic/helpers/src";
import { calculateTotalWidth } from "@cmp-src/contexts/ColumnWidthContext";

export const useDashboardData = ({
  defaultTab
}: { defaultTab: PageOptions }) => {
  const {
    userContext: { authenticatedUser, currentOffice }
  } = useAuthenticated();

  const { isBreakpoint } = useBreakpoint();

  const isMd = isBreakpoint("md");

  const {
    selectedEvent,
    setWeekDate: setDashboardWeekDate,
    page,
    setPage,
    filters,
    setSelectedEvent,
    agendaDisclosure
  } = useDashboardFilters();

  useEffect(() => {
    if (selectedEvent) {
      setPage(tabToPageOption.schedules);
    }
  }, [selectedEvent]);

  const router = useRouter();
  const [shouldRenderContent, setShouldRenderContent] = useState(false);

  const activeTab = pageOptionToTab[page || defaultTab];

  // Create a ref for measuring container width
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    filters: dashboardFilters,
    isComponentLoading,
    defaultOffice: _defaultOffice,
    setOfficesId: setOffices
  } = useTeamAttendanceFilters({
    authenticatedUser,
    currentOffice,
    onlyFilters: true
  });

  const defaultOffice = useRef(_defaultOffice);
  useEffect(() => {
    if (_defaultOffice && !defaultOffice.current) {
      defaultOffice.current = _defaultOffice;
    }
  }, [_defaultOffice]);

  // Set initial tab from URL if available
  useEffect(() => {
    if (!router.isReady) return;

    if (
      router.query.dashboard_tab &&
      typeof router.query.dashboard_tab === "string"
    ) {
    } else if (!page) {
      setPage(defaultTab);
    }
  }, [router.isReady]);

  // Always render both components, but hide one with CSS
  // This ensures the same hooks are called on every render
  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    // Check if we should redirect to the map
    // TODO: remove this once we have properly implemented map links
    // See: https://app.shortcut.com/tactic/story/2705/locate-button-doesn-t-filter-for-the-floor
    // See: https://tactic-hq.slack.com/archives/C07L5L6GLUF/p1737385471739359
    const [_, hash] = (router.asPath as string).split("#");
    const parsedHash = new URLSearchParams(hash);
    const resourceId = parsedHash.get("resource");
    const officeId = parsedHash.get("office");
    const floorId = parsedHash.get("floor");
    const date = parsedHash.get("date");
    const shouldGoToMap = parsedHash.get("goToMap");
    if (shouldGoToMap && resourceId && officeId && date) {
      goToMap(router, resourceId, officeId, date, floorId);
    }

    setShouldRenderContent(true);
  }, [router.isReady]);

  const locationData = useDashboardLocation({
    onDateChange: (date: string) => {
      setDashboardWeekDate(date);
    }
  });

  const isAdmin = canScheduleOthers(authenticatedUser);

  const { data: sections } = usePageSectionsQuery();
  const hasPages = sections?.length && sections?.length > 0;

  // let daysCount = locationData.availableDays?.length || 0;
  // if(!daysCount && (locationData.availableDays as any).elements) {
  //   daysCount = (locationData.availableDays as any).elements?.filter((day: IHourPost) => day.type === 'weekday').length || 0;
  // }

  // Ensure daysCount is calculated correctly even if availableDays is malformed
  // const safeDaysCount = Array.isArray(locationData.availableDays)
  //   ? locationData.availableDays.length
  //   : 0;
  // const finalDaysCount = safeDaysCount || daysCount || DEFAULT_DAYS_COUNT;
  // For now we're hardcoding the days count to 5 because Chris and Reid think it's better
  const finalDaysCount = DEFAULT_DAYS_COUNT;

  const dashboardWidth = calculateTotalWidth(finalDaysCount);

  const handleTabChange = (value: string) => {
    if (value in tabToPageOption) {
      setPage(tabToPageOption[value as keyof typeof tabToPageOption]);
    }
  };

  const SIDEBAR_WIDTH = 389;
  const WHOLE_WIDTH = `${SIDEBAR_WIDTH}px`;

  const {
    nextWeek,
    prevWeek,
    weekDate,
    setWeekDate,
    scheduleMeData,
    setOooRequest,
    oooRequest,
    onConfirmDialogCallback,
    setOnConfirmDialogCallback
  } = locationData;

  return {
    setPage,
    shouldRenderContent,
    isMd,
    activeTab,
    handleTabChange,
    containerRef,
    isComponentLoading,
    dashboardFilters,
    setOffices,
    defaultOffice: defaultOffice.current,
    selectedEvent,
    setSelectedEvent,
    agendaDisclosure,
    authenticatedUser,
    currentOffice,
    isAdmin,
    sections,
    hasPages: Boolean(hasPages),
    finalDaysCount,
    weekDate,
    setWeekDate,
    nextWeek,
    prevWeek,
    scheduleMeData,
    oooRequest,
    setOooRequest,
    onConfirmDialogCallback,
    setOnConfirmDialogCallback,
    dashboardWidth,
    WHOLE_WIDTH,
    locationData
  };
};
